import { useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import Modal from '@/shared/components/Modal';
import { useAuth } from '@auth';
import { CreationForm, PostConnectionActivation } from '@accounts';
import { usePreparationFlow, type PreparationStep } from '../hooks/usePreparationFlow';
import { type PreparationDialogSource } from '../hooks/useGuidedPreparationDialog';
import { markFunnelStage } from '@/shared/analytics';
import StageStrip from '../../stage/components/StageStrip';
import StagePicker from '../../stage/components/StagePicker';
import ConsultingOffer from '../../consulting/components/ConsultingOffer';
import NextStep from '../../stage/components/NextStep';
import SchedulePicker from '../../schedule/components/SchedulePicker';
import NoSchedules from '../../schedule/components/NoSchedules';
import NoBookableSchedules from '../../schedule/components/NoBookableSchedules';

const TITLE_BY_STEP: Partial<Record<PreparationStep['name'], string>> = {
  pick: 'preparation.picker.question',
  connect: 'accounts.create.title',
  pickSchedule: 'preparation.schedules.heading',
  noSchedules: 'accounts.create.title',
  noBookableSchedules: 'accounts.create.title',
};

interface Props {
  isOpen: boolean;
  source: PreparationDialogSource;
  onClose: () => void;
}

export default function PreparationDialog({ isOpen, source, onClose }: Props) {
  const { visaPreparation, consulting } = useAuth();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const flow = usePreparationFlow({
    stage: visaPreparation?.stage ?? null,
    consulting,
    consultingRequested: visaPreparation?.consulting_requested ?? false,
    onCompleted: () => {
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
    onDone: onClose,
  });
  const { step } = flow;
  const declareReady = () => flow.chooseStage('portal_ready');

  useEffect(() => {
    if (!isOpen) return;

    markFunnelStage('preparation_viewed', 'preparation_viewed', { source });
  }, [isOpen, source]);

  const dismiss = () => {
    markFunnelStage('preparation_dismissed', 'preparation_dismissed', { step: step.name });
    onClose();
  };

  if (step.name === 'activating') {
    return (
      <PostConnectionActivation
        countryCode={step.account.country_code}
        schedule={step.schedule}
        onActivated={flow.complete}
        onCancelled={flow.cancelActivation}
      />
    );
  }

  const titleKey = TITLE_BY_STEP[step.name];

  return (
    <Modal
      isOpen={isOpen}
      onClose={dismiss}
      title={titleKey ? intl.formatMessage({ id: titleKey }) : undefined}
      eyebrow={intl.formatMessage({ id: 'preparation.title' })}
    >
      <div className="space-y-8">
        {step.name !== 'pick' && (
          <div className="border-b border-slate-200/70 pb-6">
            <StageStrip stage={flow.stage} onSelect={flow.chooseStage} />
          </div>
        )}

        {flow.stageSaveFailed && (
          <p className="text-sm text-red-600">
            <FormattedMessage id="preparation.saveError.message" />{' '}
            <button type="button" onClick={flow.retryStageSave} className="underline underline-offset-2">
              <FormattedMessage id="preparation.saveError.retry" />
            </button>
          </p>
        )}

        {step.name === 'pick' && <StagePicker onChoose={flow.chooseStage} />}

        {step.name === 'consulting' && (
          <ConsultingOffer offer={step.offer} alreadyRequested={step.alreadyRequested} />
        )}

        {step.name === 'nextStep' && (
          <NextStep stage={step.stage} onReady={declareReady} />
        )}

        {step.name === 'connect' && (
          <CreationForm onConnected={flow.connected} analyticsSource="onboarding" />
        )}

        {step.name === 'pickSchedule' && (
          <SchedulePicker schedules={step.schedules} onChoose={flow.chooseSchedule} />
        )}

        {step.name === 'noSchedules' && (
          <NoSchedules accountId={step.account.id} onFound={flow.schedulesFound} />
        )}

        {step.name === 'noBookableSchedules' && (
          <NoBookableSchedules
            accountId={step.account.id}
            schedules={step.schedules}
            onFound={flow.schedulesFound}
          />
        )}
      </div>
    </Modal>
  );
}
